<template>
    <div class="module-header-date">
        <primevue-button
            v-tooltip.bottom="dateButtonTooltip"
            class="date-button"
            :disabled="props.disabled"
            @click="openDatePicker"
        >
            {{ props.currentDate }} {{ props.timezoneLabel }}
            <slot name="date-suffix" />
            <font-awesome-icon
                class="icon"
                data-filter-tags-width-reserve
                :icon="['far', 'calendar-days']"
            />
        </primevue-button>
        <spark-popover
            v-model:open="isOpen"
            :reference="reference"
            :placement="preferredPlacement"
            :is-modal="props.isModal"
        >
            <template #default="{ close }">
                <slot :close-date-picker="close" />
            </template>
        </spark-popover>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { type Placement } from '@floating-ui/vue';
import PrimevueButton from 'primevue/button';
import SparkPopover from '@/components/common/SparkPopover.vue';

const props = defineProps<{
    currentDate?: string;
    timezoneLabel?: string | null;
    isModal?: boolean;
    disabled?: boolean;
}>();

const reference = ref<HTMLElement | null>(null);
const isOpen = ref<boolean>(false);
const preferredPlacement = ref<Placement>('bottom-start');

const dateButtonTooltip = computed<string | undefined>(() => (
    props.disabled ? undefined : 'Change date options'
));

function isInBottomHalfOfViewport (element: HTMLElement): boolean {
    const { top } = element.getBoundingClientRect();
    const viewportMidpoint = window.innerHeight / 2;
    return top >= viewportMidpoint;
}

function resolvePlacementAnchor (trigger: HTMLElement): HTMLElement {
    return trigger.closest('.module-wrapper') ?? trigger;
}

function openDatePicker (event: Event): void {
    if (props.disabled) return;

    const trigger = event.currentTarget as HTMLElement | null;
    if (!trigger) return;

    reference.value = trigger;
    const placementAnchor = resolvePlacementAnchor(trigger);
    preferredPlacement.value = isInBottomHalfOfViewport(placementAnchor) ? 'top-start' : 'bottom-start';
    isOpen.value = true;
}

watch(isOpen, (open) => {
    if (!open) {
        reference.value = null;
    }
});

</script>

<style lang="scss" scoped>
.module-header-date {
    flex-shrink: 0;
}

.date-button {
    background: none;
    border: none;
    color: var(--primaryText);
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
    padding: 0 var(--smallSpacing);

    .icon {
        color: var(--actionColour);
        transition: all 0.4s ease-in-out;
        width: 0;
        opacity: 0;
    }
}
</style>
